import { validEmail } from "@sp-crm/core";

export interface ParsedRecipient {
    name?: string;
    email: string;
}

export interface ParseRecipientsResult {
    contacts: ParsedRecipient[];
    unparsed: string;
}

interface ParsedPart extends ParsedRecipient {
    originalText: string;
}

function isValidFormat(text: string): boolean {
    let i = 0;
    let inQuote = false;
    let inAngleBracket = false;

    while (i < text.length) {
        const char = text[i];

        if (char === '"') {
            if (!inAngleBracket) {
                inQuote = !inQuote;
            }
        } else if (char === "<" && !inQuote) {
            if (inAngleBracket) return false;
            inAngleBracket = true;
        } else if (char === ">" && !inQuote) {
            if (!inAngleBracket) return false;
            inAngleBracket = false;
        }

        i++;
    }

    return !inQuote && !inAngleBracket;
}

function parseAllRecipients(text: string): ParsedPart[] {
    if (!isValidFormat(text)) {
        return [];
    }

    const results: ParsedPart[] = [];
    let i = 0;

    while (i < text.length) {
        while (i < text.length && /[\s,;]/.test(text[i])) {
            i++;
        }

        if (i >= text.length) break;

        const startPos = i;

        if (text[i] === '"') {
            let j = i + 1;
            while (j < text.length && text[j] !== '"') {
                j++;
            }

            const name = text.substring(i + 1, j).trim();
            j++;

            while (j < text.length && /\s/.test(text[j])) {
                j++;
            }

            if (j < text.length && text[j] === "<") {
                j++;
                let k = j;
                while (k < text.length && text[k] !== ">") {
                    k++;
                }
                const email = text.substring(j, k).trim();
                const originalText = text.substring(startPos, k + 1);
                results.push({ name, email, originalText });
                i = k + 1;
            } else {
                i = j;
            }
        } else if (text[i] === "<") {
            let j = i + 1;
            while (j < text.length && text[j] !== ">") {
                j++;
            }
            const email = text.substring(i + 1, j).trim();
            const originalText = text.substring(startPos, j + 1);
            results.push({ name: "", email, originalText });
            i = j + 1;
        } else {
            let j = i;
            while (
                j < text.length &&
                text[j] !== "<" &&
                text[j] !== "," &&
                text[j] !== ";"
            ) {
                j++;
            }

            if (j < text.length && text[j] === "<") {
                const name = text.substring(i, j).trim();
                j++;
                let k = j;
                while (k < text.length && text[k] !== ">") {
                    k++;
                }
                const email = text.substring(j, k).trim();
                const originalText = text.substring(startPos, k + 1);
                results.push({ name, email, originalText });
                i = k + 1;
            } else {
                const email = text.substring(i, j).trim();
                if (email) {
                    results.push({ name: undefined, email, originalText: email });
                }
                i = j;
            }
        }
    }

    return results;
}

export function parseRecipients(text: string): ParseRecipientsResult {
    if (!isValidFormat(text)) {
        return {
            contacts: [],
            unparsed: text.trim() ? text.trim() + ", " : "",
        };
    }

    const allParsed = parseAllRecipients(text);

    const validContacts: ParsedRecipient[] = [];
    const invalidParts: string[] = [];

    allParsed.forEach(part => {
        if (validEmail(part.email)) {
            validContacts.push({ name: part.name, email: part.email });
        } else {
            invalidParts.push(part.originalText);
        }
    });

    let unparsed = "";
    if (invalidParts.length > 0) {
        unparsed = invalidParts.join(", ");
        if (invalidParts.length === 1) {
            unparsed += ", ";
        }
    }

    return {
        contacts: validContacts,
        unparsed,
    };
}
